import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setActiveListingId } from "../redux/actions/assessment";
import { requestListingImport, submitListing } from "../redux/actions/zillowImport";
import { setFunnelToken } from "../utils/auth";
import { flattenError } from "../utils/errorDisplay";
import {
  currentIntakeRetryAt,
  intakeErrorText,
  parseImportIntakeFailure,
  rememberIntakeCooldown,
} from "../utils/intakeLimits";
import {
  clearZillowImportSession,
  isListingImportActive,
  loadZillowImportSession,
  prepareZillowImportSession,
} from "../utils/zillowImportSession";
import useIntakeCooldown from "./useIntakeCooldown";

export const IMPORT_MAX_POLL_MS = 10 * 60 * 1000;

/** @param {unknown} failure */
const failureMessage = (failure) =>
  intakeErrorText(failure) ||
  flattenError(failure)
    .map(({ message }) => message)
    .join(" ") ||
  "We could not check this import. Your existing request is saved; try checking again.";

/**
 * One request at a time, one timer, and a durable identity across lost replies.
 * @param {{listingUuid?: string | null, resumePending?: boolean, onReady?: (listing: any) => void}} options
 */
const useZillowImport = ({ listingUuid = null, resumePending = false, onReady } = {}) => {
  const [statusRetryAt, setStatusRetryAt] = useState(0);
  const cooldown = useIntakeCooldown(statusRetryAt);
  const dispatch = /** @type {(action: any) => any} */ (useDispatch());
  const listing = useSelector((/** @type {any} */ state) => state.listings.currentListing);
  const activeListingId = useSelector((/** @type {any} */ state) => state.listings.activeListingId);
  const [session, setSession] = useState(() => (resumePending ? loadZillowImportSession() : null));
  const [trackedUuid, setTrackedUuid] = useState(session?.listingUuid || null);
  const [lastResponse, setLastResponse] = useState(/** @type {any} */ (null));
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(/** @type {any} */ (""));
  const [pollRevision, setPollRevision] = useState(0);
  const uuid = listingUuid || trackedUuid;
  const snapshot =
    listing?.uuid === uuid ? listing : lastResponse?.uuid === uuid ? lastResponse : null;
  const importProgress = snapshot?.import;
  const importIntakeFailure = useMemo(
    () => parseImportIntakeFailure(importProgress),
    [importProgress]
  );
  useEffect(() => {
    rememberIntakeCooldown(importIntakeFailure?.retry_at);
  }, [importIntakeFailure]);
  const statusRetryScope = useRef(uuid);
  useEffect(() => {
    if (statusRetryScope.current !== uuid) {
      statusRetryScope.current = uuid;
      setStatusRetryAt(0);
    }
  }, [uuid]);
  const mounted = useRef(false);
  const operation = useRef(/** @type {AbortController | null} */ (null));
  const stopPolling = useRef(() => {});
  const readyUuid = useRef(/** @type {string | null} */ (null));
  const current = useRef({ snapshot, uuid, activeListingId, onReady });
  current.current = { snapshot, uuid, activeListingId, onReady };
  const bootSession = useRef(session);

  const complete = useCallback((/** @type {any} */ data) => {
    const saved = loadZillowImportSession();
    if (saved && saved.listingUuid === data.uuid) clearZillowImportSession(saved.requestId);
    if (!mounted.current || readyUuid.current === data.uuid) return;
    readyUuid.current = data.uuid;
    setSession(null);
    current.current.onReady?.(data);
  }, []);

  const start = useCallback(
    async (/** @type {string} */ source) => {
      if (operation.current || currentIntakeRetryAt() > Date.now()) return;
      let saved;
      try {
        saved = prepareZillowImportSession(source);
      } catch (failure) {
        setError(failureMessage(/** @type {Error} */ (failure).message));
        return;
      }
      setSession(saved);
      setError("");
      setBusy(true);
      stopPolling.current();
      const controller = new AbortController();
      operation.current = controller;
      try {
        const result = await dispatch(
          submitListing({ zillow_url: saved.sourceUrl }, { signal: controller.signal })
        );
        if (!mounted.current || controller.signal.aborted) return;
        if (!result.ok) {
          if (!result.aborted) {
            setSession(result.session || saved);
            setError(result.error);
          }
          return;
        }
        setSession(result.session);
        setTrackedUuid(result.data.uuid);
        setLastResponse(result.data);
        if (["ready", "cancelled"].includes(result.data.import.status)) complete(result.data);
      } finally {
        if (operation.current === controller) operation.current = null;
        if (mounted.current && !controller.signal.aborted) setBusy(false);
      }
    },
    [complete, dispatch]
  );

  useEffect(() => {
    mounted.current = true;
    const saved = bootSession.current;
    if (resumePending && saved) {
      if (saved.listingUuid) {
        setFunnelToken(saved.listingUuid, saved.funnelToken);
        dispatch(setActiveListingId(saved.listingUuid));
      } else if (!saved.manualRetryRequired) {
        // A lost POST acknowledgement has no listing UUID. Replay exactly the
        // saved pair once; failures wait for an explicit Resume, not a new job.
        void start(saved.sourceUrl);
      }
    }
    return () => {
      mounted.current = false;
      operation.current?.abort();
      operation.current = null;
      stopPolling.current();
    };
  }, [dispatch, resumePending, start]);

  useEffect(() => {
    if (!uuid || busy || activeListingId !== uuid) return undefined;
    let stopped = false;
    let timer = /** @type {ReturnType<typeof setTimeout> | undefined} */ (undefined);
    let controller = /** @type {AbortController | null} */ (null);
    let failures = 0;
    const started = Date.now();
    const stop = () => {
      stopped = true;
      if (timer !== undefined) clearTimeout(timer);
      controller?.abort();
    };
    stopPolling.current = stop;
    const poll = async () => {
      if (stopped) return;
      if (Date.now() - started >= IMPORT_MAX_POLL_MS) {
        setChecking(false);
        setError(
          "This import is taking longer than usual. Check progress again or stop it and continue manually."
        );
        return;
      }
      controller = new AbortController();
      setChecking(true);
      const result = await dispatch(
        requestListingImport(uuid, "status", { signal: controller.signal })
      );
      if (
        stopped ||
        !mounted.current ||
        current.current.uuid !== uuid ||
        (current.current.activeListingId && current.current.activeListingId !== uuid)
      )
        return;
      setChecking(false);
      if (result.ok) {
        failures = 0;
        setStatusRetryAt(0);
        setError("");
        setLastResponse(result.data);
        if (["ready", "cancelled"].includes(result.data.import.status)) {
          complete(result.data);
          return;
        }
        if (!isListingImportActive(result.data)) return;
        const delay = Number(result.data.import.retry_after_seconds) * 1000;
        timer = setTimeout(
          poll,
          Number.isFinite(delay) && delay > 0 ? Math.min(30000, Math.max(1000, delay)) : 3000
        );
      } else if (!result.aborted) {
        setError(result.error);
        if (result.status === 429) setStatusRetryAt(result.error?.retry_at || 0);
        if ([401, 403, 404, 429].includes(result.status)) return;
        failures += 1;
        timer = setTimeout(poll, Math.min(30000, 3000 * 2 ** Math.min(failures - 1, 4)));
      }
    };
    const known = current.current.snapshot;
    if (known && !isListingImportActive(known) && pollRevision === 0) {
      if (["ready", "cancelled"].includes(known.import?.status)) complete(known);
    } else {
      void poll();
    }
    return stop;
  }, [uuid, busy, activeListingId, pollRevision, dispatch, complete]);

  const changeImport = useCallback(
    async (/** @type {"retry" | "cancel"} */ action) => {
      const target = current.current.uuid;
      if (
        !target ||
        operation.current ||
        (action === "retry" && currentIntakeRetryAt() > Date.now())
      )
        return;
      stopPolling.current();
      const controller = new AbortController();
      operation.current = controller;
      setBusy(true);
      setChecking(false);
      setError("");
      try {
        const result = await dispatch(
          requestListingImport(target, action, { signal: controller.signal })
        );
        if (
          !mounted.current ||
          controller.signal.aborted ||
          current.current.uuid !== target ||
          (current.current.activeListingId && current.current.activeListingId !== target)
        )
          return;
        if (!result.ok) {
          setError(result.error);
          return;
        }
        setLastResponse(result.data);
        if (action === "cancel") {
          if (isListingImportActive(result.data)) {
            setError("The import has not stopped yet. Check progress before continuing manually.");
            return;
          }
          complete(result.data);
        } else if (result.data.import.status === "ready") {
          complete(result.data);
        }
      } finally {
        if (operation.current === controller) operation.current = null;
        if (mounted.current && !controller.signal.aborted) {
          setBusy(false);
        }
      }
    },
    [complete, dispatch]
  );

  const resume = () => {
    if (operation.current) return;
    if (uuid) {
      if (statusRetryAt > Date.now()) return;
      setError("");
      setPollRevision((revision) => revision + 1);
    } else {
      const saved = loadZillowImportSession();
      if (saved && currentIntakeRetryAt() <= Date.now()) void start(saved.sourceUrl);
    }
  };

  // Abandon an import that never reached the server. Without this the entry
  // page is a dead end: a saved session replaces the address form, and the
  // only control offered is "Resume import", which re-runs the same failure
  // (Trello gWHnPJB0). Guarded on there being no uuid — once a listing exists
  // there is something server-side to stop, which is what continueManually
  // does, and dropping the record locally would orphan it.
  const discard = () => {
    if (uuid || operation.current) return;
    const saved = loadZillowImportSession();
    if (saved) clearZillowImportSession(saved.requestId);
    stopPolling.current();
    setSession(null);
    setError("");
  };

  return {
    snapshot,
    session,
    uuid,
    busy,
    checking,
    error:
      error || importIntakeFailure || cooldown.blocked
        ? failureMessage(error || importIntakeFailure)
        : "",
    cooldown,
    statusCheckingBlocked: statusRetryAt > cooldown.now,
    visible: !!session || !!uuid,
    recoveryWarning:
      session && !session.persisted
        ? "This browser could not save recovery information. Keep this tab open; refreshing could lose access to this import."
        : "",
    start,
    resume,
    retry: () => changeImport("retry"),
    continueManually: () => changeImport("cancel"),
    discard,
  };
};

export default useZillowImport;
